import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Target, TrendingUp, Calendar, Plus, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Goal {
  id: string;
  goal_name: string;
  goal_type: string;
  target_metric: string;
  target_value: number;
  current_value: number;
  deadline: string;
  status: string;
  created_at: string;
  agent_goal_strategies: any[];
}

export function AgentGoalsDashboard({ orgId }: { orgId: string }) {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [editing, setEditing] = useState<Goal | null>(null);
  const [newGoal, setNewGoal] = useState({
    goal_name: '',
    goal_type: 'close_jobs',
    target_metric: 'jobs',
    target_value: 10,
    deadline: '',
  });

  useEffect(() => {
    loadGoals();
  }, [orgId]);

  const loadGoals = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('agent-goals', {
        body: { action: 'get_goals', data: { org_id: orgId } },
      });

      if (error) throw error;
      setGoals(data.goals || []);
    } catch (error) {
      console.error('Error loading goals:', error);
      toast.error('Failed to load goals');
    } finally {
      setLoading(false);
    }
  };

  const createGoal = async () => {
    if (!newGoal.goal_name || !newGoal.deadline) {
      toast.error('Please fill in all fields');
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('agent-goals', {
        body: {
          action: 'create_goal',
          data: {
            org_id: orgId,
            ...newGoal,
          },
        },
      });

      if (error) throw error;
      
      toast.success('Goal created with AI-generated strategies!');
      await loadGoals();
      
      setNewGoal({
        goal_name: '',
        goal_type: 'close_jobs',
        target_metric: 'jobs',
        target_value: 10,
        deadline: '',
      });
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    } finally {
      setCreating(false);
    }
  };

  const deleteGoal = async (goalId: string) => {
    setDeleting(goalId);
    try {
      const { error } = await supabase.functions.invoke('agent-goals', {
        body: {
          action: 'delete_goal',
          data: { goal_id: goalId },
        },
      });

      if (error) throw error;
      
      toast.success('Goal deleted successfully');
      await loadGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Failed to delete goal');
    } finally {
      setDeleting(null);
    }
  };

  const updateGoal = async () => {
    if (!editing) return;

    try {
      const { error } = await supabase.functions.invoke('agent-goals', {
        body: {
          action: 'update_goal',
          data: {
            goal_id: editing.id,
            goal_name: editing.goal_name,
            target_value: editing.target_value,
            deadline: editing.deadline,
          },
        },
      });

      if (error) throw error;
      
      toast.success('Goal updated successfully');
      await loadGoals();
      setEditing(null);
    } catch (error) {
      console.error('Error updating goal:', error);
      toast.error('Failed to update goal');
    }
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return 'bg-green-500';
    if (percentage >= 75) return 'bg-blue-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      active: 'default',
      completed: 'secondary',
      overdue: 'destructive',
      cancelled: 'outline',
    };
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Business Goals</h2>
          <p className="text-muted-foreground">
            AI-powered goal tracking and strategic planning
          </p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Goal
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Goal</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Goal Name</Label>
                <Input
                  placeholder="e.g., Close 10 more jobs this quarter"
                  value={newGoal.goal_name}
                  onChange={(e) => setNewGoal({ ...newGoal, goal_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Goal Type</Label>
                <Select
                  value={newGoal.goal_type}
                  onValueChange={(value) => {
                    const metricMap: Record<string, string> = {
                      close_jobs: 'jobs',
                      increase_revenue: 'dollars',
                      reduce_cycle_time: 'days'
                    };
                    setNewGoal({ 
                      ...newGoal, 
                      goal_type: value,
                      target_metric: metricMap[value] || 'jobs'
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="close_jobs">Close More Jobs</SelectItem>
                    <SelectItem value="increase_revenue">Increase Revenue</SelectItem>
                    <SelectItem value="reduce_cycle_time">Reduce Cycle Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Target Value</Label>
                <Input
                  type="number"
                  value={newGoal.target_value}
                  onChange={(e) => setNewGoal({ ...newGoal, target_value: parseInt(e.target.value) })}
                />
              </div>
              <div className="space-y-2">
                <Label>Deadline</Label>
                <Input
                  type="date"
                  value={newGoal.deadline}
                  onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                />
              </div>
              <Button onClick={createGoal} disabled={creating} className="w-full">
                {creating ? 'Creating...' : 'Create Goal with AI Strategies'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading goals...</div>
      ) : goals.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              No goals yet. Create your first goal to get started with AI-powered planning.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2">
          {goals.map((goal) => {
            const progress = goal.target_value > 0
              ? Math.round((goal.current_value / goal.target_value) * 100)
              : 0;

            return (
              <Card key={goal.id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 flex-1">
                      <CardTitle className="text-lg">{goal.goal_name}</CardTitle>
                      <CardDescription>
                        {goal.current_value} / {goal.target_value} {goal.target_metric}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(goal.status)}
                      <Dialog open={editing?.id === goal.id} onOpenChange={(open) => !open && setEditing(null)}>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={() => setEditing(goal)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Edit Goal</DialogTitle>
                          </DialogHeader>
                          {editing && (
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <Label>Goal Name</Label>
                                <Input
                                  value={editing.goal_name}
                                  onChange={(e) => setEditing({ ...editing, goal_name: e.target.value })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Target Value</Label>
                                <Input
                                  type="number"
                                  value={editing.target_value}
                                  onChange={(e) => setEditing({ ...editing, target_value: parseInt(e.target.value) })}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label>Deadline</Label>
                                <Input
                                  type="date"
                                  value={editing.deadline}
                                  onChange={(e) => setEditing({ ...editing, deadline: e.target.value })}
                                />
                              </div>
                              <Button onClick={updateGoal} className="w-full">
                                Update Goal
                              </Button>
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" disabled={deleting === goal.id}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Goal</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure? This will permanently delete "{goal.goal_name}" and all its strategies.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteGoal(goal.id)}>
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{progress}%</span>
                    </div>
                    <Progress value={progress} className={getProgressColor(progress)} />
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    Deadline: {new Date(goal.deadline).toLocaleDateString()}
                  </div>

                  {goal.agent_goal_strategies?.length > 0 && (
                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <TrendingUp className="h-4 w-4" />
                        AI Strategies ({goal.agent_goal_strategies.length})
                      </div>
                      {goal.agent_goal_strategies.slice(0, 2).map((strategy: any, idx: number) => (
                        <div key={idx} className="text-sm text-muted-foreground pl-6">
                          • {strategy.strategy_name}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
